import { AI_CHAT_FEEDBACK_REACTION } from '../../models/ai-chat-feedback';
import { AiChatMessageRole, isAiChatUserRole } from '../../models/ai-chat-message';
import { Theme, keyframes, styled } from '../../theme';
import { AiChatFeedbackReaction } from '../../types';
import FlexColumn from '../FlexColumn';
import FlexRow from '../FlexRow';

// Shared bubble primitives — reused across message/feedback bubbles and the filters card

const fadeInUp = keyframes({
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
});

const chipFadeIn = keyframes({
    from: { opacity: 0, transform: 'translateY(2px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
});

export const PREMIUM_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

export const rowEnterAnimation = {
    animation: `${fadeInUp} 320ms ${PREMIUM_EASE} both`,
    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
    },
};

export const ASSISTANT_BUBBLE_MAX_WIDTH = 693;
export const ASSISTANT_BUBBLE_SPLIT_MAX_WIDTH = 360;

export const assistantBubbleSurface = (theme: Theme) => ({
    padding: 12,
    borderRadius: theme.borderRadius.base,
    background: theme.color.white,
    color: theme.color.jetBlack,
    fontFamily: theme.typography.fontFamily,
    boxSizing: 'border-box' as const,
});

export const messageBubbleText = (theme: Theme) => ({
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: theme.typography.fontWeight.roman,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
});

export const feedbackReactionColor = (theme: Theme, reaction?: AiChatFeedbackReaction | null) => {
    if (reaction === AI_CHAT_FEEDBACK_REACTION.LIKE) {
        return theme.color.green1;
    }
    if (reaction === AI_CHAT_FEEDBACK_REACTION.DISLIKE) {
        return theme.color.red;
    }
    return theme.color.gray70;
};

export const highlightBorder = (theme: Theme, reaction?: AiChatFeedbackReaction | null) => {
    const color = feedbackReactionColor(theme, reaction);

    return reaction ? `1px solid ${color}` : undefined;
};

export const AssistantSideRow = styled(FlexRow)({
    width: '100%',
    paddingLeft: 12,
    paddingRight: 28,
    justifyContent: 'flex-start',
    ...rowEnterAnimation,
});

export const UserSideRow = styled(FlexRow)({
    width: '100%',
    paddingLeft: 28,
    paddingRight: 12,
    justifyContent: 'flex-end',
    ...rowEnterAnimation,
});

export const INLINE_CHIP_MAX_WIDTH = 220;

// Generic pill: used by filter-value chips (FilterChip.tsx) and feedback-reaction chips (FeedbackMessage.tsx)
export const Chip = styled('div', {
    shouldForwardProp: (prop) =>
        prop !== 'animationDelay' && prop !== 'feedbackReaction' && prop !== 'isInline',
})<{
    animationDelay?: number;
    feedbackReaction?: AiChatFeedbackReaction | null;
    isInline?: boolean;
}>(({ theme, animationDelay = 0, feedbackReaction, isInline = false }) => {
    const baseChipStyles = {
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        gap: 8,
        fontSize: 14,
        lineHeight: '20px',
        wordBreak: 'break-word',
    };

    if (feedbackReaction !== undefined) {
        const color = feedbackReactionColor(theme, feedbackReaction);

        return {
            ...baseChipStyles,
            justifyContent: 'center',
            padding: '10px 12px',
            borderRadius: 34,
            border: `1px solid ${color}`,
            background: 'transparent',
            color,
            fontFamily: theme.typography.fontFamily,
            fontWeight: theme.typography.fontWeight.medium,
            whiteSpace: 'normal',
        };
    }

    const defaultChipStyles = {
        ...baseChipStyles,
        padding: '4px 8px',
        borderRadius: theme.borderRadius.base,
        background: theme.color.backgroundGray,
        color: theme.color.jetBlack,
    };

    if (isInline) {
        return {
            ...defaultChipStyles,
            cursor: 'pointer',
            flexShrink: 0,
            maxWidth: INLINE_CHIP_MAX_WIDTH,
            whiteSpace: 'nowrap',
            wordBreak: 'normal',
        };
    }

    return {
        ...defaultChipStyles,
        animation: `${chipFadeIn} 160ms ease-out both`,
        animationDelay: `${animationDelay}ms`,
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
        },
    };
});

// Message Bubble — MessageBubble.tsx, FeedbackMessage.tsx

export const BubbleMessageText = styled.div(({ theme }) => ({
    width: '100%',
    ...messageBubbleText(theme),
}));

export const MessageBubbleRow = styled(FlexRow)<{ variant: AiChatMessageRole }>(({ variant }) => {
    const isUser = isAiChatUserRole(variant);

    return {
        width: '100%',
        paddingLeft: isUser ? 28 : 12,
        paddingRight: isUser ? 12 : 28,
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        ...rowEnterAnimation,
    };
});

export const MessageBubbleBody = styled.div<{
    variant: AiChatMessageRole;
    isSplitMode: boolean;
    isFullWidth?: boolean;
    highlightReaction?: AiChatFeedbackReaction | null;
}>(({ theme, variant, isSplitMode, isFullWidth, highlightReaction }) => ({
    width: isFullWidth ? '100%' : undefined,
    maxWidth: isSplitMode ? ASSISTANT_BUBBLE_SPLIT_MAX_WIDTH : ASSISTANT_BUBBLE_MAX_WIDTH,
    borderRadius: theme.borderRadius.base,
    border: highlightBorder(theme, highlightReaction),
    background: isAiChatUserRole(variant) ? theme.color.gray15 : theme.color.white,
    color: theme.color.jetBlack,
    fontFamily: theme.typography.fontFamily,
    padding: 12,
    ...messageBubbleText(theme),
}));

// Markdown Content — MarkdownMessageContent.tsx

export const MarkdownMessageContentRoot = styled.div(({ theme }) => ({
    whiteSpace: 'normal',
    '& p, & li, & blockquote': {
        whiteSpace: 'pre-wrap',
    },
    '& > *:last-child': {
        marginBottom: 0,
    },
    '& p': {
        margin: '0 0 8px',
    },
    '& ul, & ol': {
        margin: '0 0 8px',
        paddingLeft: 20,
    },
    '& li': {
        margin: 0,
    },
    '& li > p': {
        margin: 0,
    },
    '& li > ul, & li > ol': {
        margin: 0,
    },
    '& ul': {
        listStyleType: 'disc',
    },
    '& ol': {
        listStyleType: 'decimal',
    },
    '& blockquote': {
        margin: '0 0 8px',
        paddingLeft: 10,
        borderLeft: `2px solid ${theme.color.gray40}`,
        color: theme.color.gray70,
    },
    '& code': {
        fontFamily: 'monospace',
        fontSize: 14,
        background: theme.color.surface,
        borderRadius: 3,
        padding: '1px 4px',
        whiteSpace: 'break-spaces',
    },
    '& pre': {
        margin: '0 0 8px',
        padding: 8,
        overflowX: 'auto',
        background: theme.color.surface,
        borderRadius: theme.borderRadius.base,
    },
    '& pre code': {
        padding: 0,
        background: 'transparent',
        whiteSpace: 'pre',
    },
}));

// Feedback Bubble — FeedbackBubble.tsx

export const FeedbackBubbleContainer = styled(FlexColumn)<{ maxWidth: number }>(
    ({ theme, maxWidth }) => ({
        ...assistantBubbleSurface(theme),
        maxWidth,
        width: '100%',
    })
);

// Filter Chips & Tooltips — FilterChip.tsx, FilterChips.tsx, FiltersCard.tsx, AppliedFiltersInfo.tsx

export const ChipsRow = styled(FlexRow)(({ theme }) => ({
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    gap: 8,
    width: '100%',
    borderRadius: theme.borderRadius.base,
    overflow: 'hidden',
}));
